import {useMemo} from 'react'
import type {ConversationComment} from '../../page-data/payloads/conversation-comments'
import {useThreads} from '../../stores/selectors'
import {filterThreads} from './utils/filter-items'
import type {CommentsFilterState} from './use-comments-filter-state'

interface UseFilteredThreadsForReviewsOptions {
  filterState: CommentsFilterState
  filteredComments: ConversationComment[]
}

export function useFilteredThreadsForReviews({filteredComments, filterState}: UseFilteredThreadsForReviewsOptions) {
  const allThreads = useThreads()

  return useMemo(() => {
    const reviews = filteredComments.filter(c => c.type === 'review_summary')
    const allReviewThreadIds = new Set(reviews.flatMap(r => r.reviewThreadIds))

    const unfilteredReviewThreads = allThreads.filter(t => allReviewThreadIds.has(t.id))
    const loadedThreadIds = new Set(unfilteredReviewThreads.map(thread => thread.id))
    const reviewsWithMissingThreads = new Set(
      reviews.filter(review => review.reviewThreadIds.some(id => !loadedThreadIds.has(id))).map(review => review.id),
    )
    const filteredReviewThreads = filterThreads(unfilteredReviewThreads, filterState)

    const filteredReviewThreadsById = new Map(filteredReviewThreads.map(thread => [thread.id, thread]))
    const filteredReviewThreadsByReviewId = new Map(
      reviews.map(r => [
        r.id,
        r.reviewThreadIds
          .map(threadId => filteredReviewThreadsById.get(threadId))
          .filter(thread => thread !== undefined),
      ]),
    )

    return {filteredReviewThreadsByReviewId, filteredReviewThreads, reviewsWithMissingThreads}
  }, [allThreads, filterState, filteredComments])
}
